// Gate de anticipo para la agenda pública -- lógica PURA y sin dependencias.
//
// Vive fuera de PublicBookingPage a propósito: la regla de "cuándo puede
// crearse una reserva sin haber cobrado" es de producto, no de presentación, y
// tiene que poder verificarse sin montar un componente de 2.400 líneas.
//
// Regla, sin excepciones:
//   sin anticipo requerido                  -> reserva directa
//   anticipo requerido + MP disponible      -> solo pago con MercadoPago
//   anticipo requerido + MP NO disponible   -> NO se reserva, se bloquea

export interface PaymentConfigLike {
  require_deposit?: boolean;
  deposit_type?: string;
  deposit_value?: number;
}

export interface MpConfigLike {
  isActive: boolean;
  publicKey: string;
}

export type BookingCta = 'direct_book' | 'pay_with_mp' | 'blocked_no_payment';

/**
 * ¿El anticipo bloquea la creación directa de la reserva?
 *
 * Exige AMBAS cosas: que el negocio lo haya marcado como obligatorio y que el
 * importe calculado sea mayor que cero. Un `require_deposit = true` con importe
 * 0 (p. ej. porcentaje sobre servicios sin precio) no bloquea nada, porque no
 * habría nada que cobrar.
 */
export function isDepositRequired(
  paymentConfig: PaymentConfigLike | null | undefined,
  depositAmount: number,
): boolean {
  return paymentConfig?.require_deposit === true && depositAmount > 0;
}

/** ¿Se puede cobrar online ahora mismo? */
export function isMpAvailable(mpConfig: MpConfigLike | null | undefined): boolean {
  return Boolean(mpConfig?.isActive && mpConfig?.publicKey);
}

/**
 * Qué acción ofrecer al cliente.
 *
 * `blocked_no_payment` es el caso que provocó el incidente: antes caía en la
 * rama de reserva directa y creaba la cita sin cobrar, mostrando además un
 * botón que decía "Pagar abono $X".
 */
export function resolveBookingCta(input: {
  paymentConfig: PaymentConfigLike | null | undefined;
  depositAmount: number;
  mpConfig: MpConfigLike | null | undefined;
  mpPaymentDone: boolean;
}): BookingCta {
  const depositRequired = isDepositRequired(input.paymentConfig, input.depositAmount);

  if (!depositRequired) return 'direct_book';
  if (input.mpPaymentDone) return 'direct_book';
  if (!isMpAvailable(input.mpConfig)) return 'blocked_no_payment';
  return 'pay_with_mp';
}

/**
 * Importe de anticipo que debe PERSISTIRSE en la cita.
 *
 * Si el anticipo no es obligatorio no se cobra nunca (el gate ni siquiera
 * ofrece pagar), así que registrar el importe configurado dejaba citas con
 * "$100 pendiente" que nadie iba a cobrar. El importe configurado sigue siendo
 * válido como referencia en pantalla; lo que no puede es acabar en la fila.
 */
export function effectiveDepositAmount(
  paymentConfig: PaymentConfigLike | null | undefined,
  depositAmount: number,
): number {
  return isDepositRequired(paymentConfig, depositAmount) ? depositAmount : 0;
}

/**
 * Guarda de `handleBook`: la reserva directa no puede crearse mientras el
 * anticipo obligatorio no esté pagado, aunque el render dejara el camino
 * alcanzable por un cambio futuro.
 */
export function canCreateBookingDirectly(input: {
  paymentConfig: PaymentConfigLike | null | undefined;
  depositAmount: number;
  mpPaymentDone: boolean;
}): boolean {
  if (!isDepositRequired(input.paymentConfig, input.depositAmount)) return true;
  return input.mpPaymentDone;
}

/**
 * Etiqueta del importe en el modal de confirmación.
 *
 * Solo se afirma "pagado" con el resultado VERIFICADO del flujo
 * (mp-verify-deposit-and-book validó `approved` contra /v1/payments). Nunca se
 * infiere del importe configurado ni del query string de retorno.
 */
export function depositBadgeLabel(depositPaidConfirmed: boolean): 'Abono pagado' | 'Abono pendiente' {
  return depositPaidConfirmed ? 'Abono pagado' : 'Abono pendiente';
}
